import type { ClassroomBody, GlowApiError } from '@innovamat/glow-api-client';
import {
  usePutClassroomMutation,
  useUpdateDigitalPracticeAccessMutation,
  useUpdateTimezoneMutation,
} from '@innovamat/glow-api-client';
import { useQueryClient } from '@tanstack/react-query';
import { updateOrganizationTimezone } from '../cache-actions';
import { DigitalPracticeAccess } from '../modules/classroom/classroom-home-access';

type UsePutClassroom = {
  handlePutClassroom: (
    body: ClassroomBody,
    timezone?: string,
    digitalPracticeAccess?: DigitalPracticeAccess,
    studentsPropagation?: boolean
  ) => void;
  isPending: boolean;
};

type Props = {
  onSuccess?: (classroomBody: ClassroomBody) => void;
  onError?: (errors: GlowApiError) => void;
};

function usePutClassroom({ onSuccess, onError }: Props): UsePutClassroom {
  const { mutate, isPending } = usePutClassroomMutation<GlowApiError>();
  const { mutate: updateTimezoneMutation, isPending: isPendingTimezone } =
    useUpdateTimezoneMutation<GlowApiError>();
  const {
    mutate: updateDigitalPracticeAccess,
    isPending: isPendingDigitalPracticeAccess,
  } = useUpdateDigitalPracticeAccessMutation<GlowApiError>();

  const queryClient = useQueryClient();

  const handlePutClassroom = (
    body: ClassroomBody,
    timezone?: string | null,
    digitalPracticeAccess?: DigitalPracticeAccess,
    studentsPropagation?: boolean
  ): void => {
    let successCount = 0;

    const checkSuccess = () => {
      successCount += 1;
      if (
        successCount === 2 ||
        (successCount === 1 && (!timezone || !digitalPracticeAccess))
      ) {
        onSuccess?.(body);
      }
    };

    mutate(
      {
        classroom: body,
      },
      {
        onSuccess() {
          if (!timezone && !digitalPracticeAccess) {
            onSuccess?.(body);
            return;
          }

          if (digitalPracticeAccess) {
            updateDigitalPracticeAccess(
              {
                body: {
                  digitalPracticeAccess,
                  classroomId: body.id!,
                  studentsPropagation,
                },
              },
              {
                onSuccess: checkSuccess,
                onError(errors) {
                  onError?.(errors);
                },
              }
            );
          } else {
            successCount += 1;
          }

          if (timezone) {
            updateTimezoneMutation(
              {
                body: {
                  timeZone: timezone,
                  organizationId: body.organizationId!,
                },
              },
              {
                onSuccess() {
                  checkSuccess();
                  updateOrganizationTimezone({
                    queryClient,
                    orgId: body.organizationId!,
                    timezone,
                  });
                },
                onError(errors) {
                  onError?.(errors);
                },
              }
            );
          } else {
            successCount += 1;
          }
        },
        onError(errors) {
          onError?.(errors);
        },
      }
    );
  };

  return {
    handlePutClassroom,
    isPending: isPending || isPendingTimezone || isPendingDigitalPracticeAccess,
  };
}

export { usePutClassroom };
